import React from "react";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";

import { cloneDeep } from "lodash";
import { Box, Chip, Tooltip, Divider, Checkbox } from "@mui/material";
import { ThemeProvider } from "@mui/material";
import { globalColors } from "../../../Utils/colors";

import {
  StyledDropDownMenuItem,
  StyledListItemIcon,
  StyledListItemText,
  StyledMenuItem,
  getDropDownMenuProps,
  getDropDownTheme,
} from "./styled";

import {
  CustomIconRendererProps,
  CustomMenuItemProps,
  CustomizedDropdownProps,
  DropDownDataProviderItemType,
} from "./type";

const CustomIconRenderer = ({ iconRenderer }: CustomIconRendererProps) => {
  return iconRenderer ? (
    <StyledListItemIcon>{iconRenderer()}</StyledListItemIcon>
  ) : (
    <React.Fragment />
  );
};

const CustomMenuItem = ({
  handleChange,
  handleClose,
  iconRenderer,
  itemlabel,
  itemvalue,
  isSelected,
  index,
  disabled,
  isMulti,
  value,
}: CustomMenuItemProps) => {
  return (
    <StyledMenuItem
      key={index}
      disableRipple
      disabled={disabled}
      isSelected={isSelected}
      onClick={() => {
        handleChange(itemvalue);
        if (!isMulti) {
          handleClose();
        }
      }}
      value={value}
    >
      <CustomIconRenderer iconRenderer={iconRenderer} />
      <StyledListItemText title={itemlabel} disableTypography>
        {itemlabel}
      </StyledListItemText>
    </StyledMenuItem>
  );
};
const CustomMenuItemMultiSelect = ({
  handleChange,
  handleClose,
  iconRenderer,
  itemlabel,
  itemvalue,
  isSelected,
  index,
  disabled,
  isMulti,
}: CustomMenuItemProps) => {
  return (
    <div className="checkbox-container">
      <Checkbox
        checked={isSelected}
        sx={{
          color: '#000000',
          '&.Mui-checked': {
            color: '#000000',
          },
        }}
        onClick={() => {
          handleChange(itemvalue);

          if (!isMulti) {
            handleClose();
          }
        }}
      />
      <StyledMenuItem
        key={index}
        disableRipple
        disabled={disabled}
        isSelected={isSelected}
        onClick={() => {
          handleChange(itemvalue);
          if (!isMulti) {
            handleClose();
          }
        }}
      >
        <CustomIconRenderer iconRenderer={iconRenderer} />
        <StyledListItemText title={itemlabel} disableTypography>
          {itemlabel}
        </StyledListItemText>
      </StyledMenuItem>
    </div>
  );
};

export default function CustomizedSingleSelectDropdown({
  dataProvider,
  value,
  setValue,
  startAdornment,
  error,
  disabled,
  fullWidth,
  isMulti = false,
  placeholder,
}: CustomizedDropdownProps) {
  const [open, setOpen] = React.useState<boolean>(false);

  const handleChange = (changeValue: string | any) => {
    if (isMulti) {
      if (value.includes(changeValue)) {
        const data = value.filter((item: string) => item !== changeValue);

        return setValue(data);
      } else {
        const newValue = cloneDeep(value);
        newValue.push(changeValue);

        return setValue(newValue);
      }
    } else {
      setValue(changeValue);
    }
  };

  const handleSelectAll = () => {
    if (isMulti && dataProvider) {
      const allValues = dataProvider.map((item: DropDownDataProviderItemType) => item.value);
      const allSelected = allValues.every((val: string) => value.includes(val));

      if (allSelected) {
        // Deselect all
        setValue([]);
      } else {
        // Select all
        setValue(allValues);
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleOpen = () => {
    setOpen(true);
  };

  return (
    <ThemeProvider theme={getDropDownTheme(fullWidth)}>
      <FormControl className="single-select-drop-container">
        <Tooltip title={!dataProvider?.length ? "No data found" : ""}>
          <Select
            multiline={isMulti}
            multiple={isMulti}
            displayEmpty={!!placeholder}
            MenuProps={{
              ...getDropDownMenuProps(),
              disableScrollLock: true,
              container: document.body,
              PaperProps: {
                style: { maxHeight: 300 }
              },
              MenuListProps: {
                disablePadding: true,
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
            SelectDisplayProps={{ style: { padding: isMulti ? 0 : 8 } }}
            open={open}
            value={!dataProvider?.length ? (isMulti ? [] : "") : value}
            disabled={disabled || !dataProvider?.length}
            onClose={handleClose}
            error={error}
            onOpen={handleOpen}
            startAdornment={
              startAdornment ? (
                <InputAdornment position="start">
                  {startAdornment}
                </InputAdornment>
              ) : null
            }
            renderValue={(selectedValue) => {
              const selectedItems = dataProvider?.filter(
                (item: DropDownDataProviderItemType) =>
                  isMulti
                    ? selectedValue?.includes(item.value)
                    : String(item.value) === String(selectedValue)
              ) || [];

              if (selectedItems.length >= 1) {
                if (!isMulti) {
                  const selectedItem = selectedItems[0];

                  return (
                    <StyledDropDownMenuItem isMulti={isMulti} disableRipple>
                      <CustomIconRenderer
                        iconRenderer={selectedItem?.iconRenderer}
                      />
                      <StyledListItemText
                        title={selectedItem?.label}
                        disableTypography
                      >
                        {selectedItem?.label}
                      </StyledListItemText>
                    </StyledDropDownMenuItem>
                  );
                } else {
                  const firstItem = selectedItems[0];
                  const remainingCount = selectedItems.length - 1;

                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Chip
                        variant="outlined"
                        label={
                          <StyledDropDownMenuItem
                            isMulti={isMulti}
                            disableRipple
                          >
                            <CustomIconRenderer
                              iconRenderer={firstItem?.iconRenderer}
                            />
                            <StyledListItemText
                              title={firstItem?.label}
                              disableTypography
                            >
                              {firstItem?.label}
                            </StyledListItemText>
                          </StyledDropDownMenuItem>
                        }
                      />
                      {remainingCount > 0 && (
                        <span style={{ color: globalColors.textLightGrey, fontSize: "12px" }}>
                          +{remainingCount}
                        </span>
                      )}
                    </Box>
                  );
                }
              } else {
                if (placeholder) {
                  return <span style={{ color: globalColors.textLightGrey }}>{placeholder}</span>;
                }
                return <React.Fragment />;
              }
            }}
          >
            {isMulti && dataProvider && dataProvider.length > 0 && (
              <>
                <StyledMenuItem
                  disableRipple
                  onClick={handleSelectAll}
                  isSelected={false}
                >
                  <Checkbox
                    checked={dataProvider.every((item: DropDownDataProviderItemType) =>
                      value.includes(item.value)
                    )}
                    indeterminate={
                      value.length > 0 &&
                      value.length < dataProvider.length
                    }
                    sx={{
                      padding: '0 8px 0 0',
                      color: '#000000',
                      '&.Mui-checked': {
                        color: '#000000',
                      },
                      '&.MuiCheckbox-indeterminate': {
                        color: '#000000',
                      },
                    }}
                  />
                  <StyledListItemText disableTypography>
                    Select All
                  </StyledListItemText>
                </StyledMenuItem>
                <Divider />
              </>
            )}
            {isMulti && dataProvider?.map(
              (item: DropDownDataProviderItemType, index: number) => {
                return (
                  <CustomMenuItemMultiSelect
                    index={index}
                    iconRenderer={item.iconRenderer}
                    key={item.value}
                    itemlabel={item.label}
                    itemvalue={item.value}
                    handleChange={handleChange}
                    handleClose={handleClose}
                    disabled={item.disabled}
                    isMulti={isMulti}
                    isSelected={
                      isMulti
                        ? value.includes(String(item.value))
                        : String(value) === String(item.value)
                    }
                    value={item.value}
                  />
                );
              }
            )}
            {!isMulti && dataProvider?.map(
              (item: DropDownDataProviderItemType, index: number) => {
                return (
                  <CustomMenuItem
                    index={index}
                    iconRenderer={item.iconRenderer}
                    key={item.value}
                    itemlabel={item.label}
                    itemvalue={item.value}
                    handleChange={handleChange}
                    handleClose={handleClose}
                    disabled={item.disabled}
                    isMulti={isMulti}
                    isSelected={
                      isMulti
                        ? value.includes(String(item.value))
                        : String(value) === String(item.value)
                    }
                    value={item.value}
                  />
                );
              }
            )}
          </Select>
        </Tooltip>
      </FormControl>
    </ThemeProvider>
  );
}
